import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Code as CodeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { SchoolWebsiteData } from '../../hooks/useSchools';
import { useTranslation } from 'react-i18next';

interface SchoolWebsiteFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: SchoolWebsiteData;
  onSubmit: (websiteData: SchoolWebsiteData) => void;
  isEdit: boolean;
  schoolId: number;
}

const SchoolWebsiteFormModal: React.FC<SchoolWebsiteFormModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
  schoolId
}) => {
  const { t } = useTranslation();

  const initialFormState: SchoolWebsiteData = {
    schoolId,
    title: '',
    description: '',
    aboutSection: '',
    contactInfo: {
      address: '',
      phone: '',
      email: '',
      website: ''
    },
    features: [''],
    galleryImages: [],
    customCss: '',
    customJs: '',
    isActive: true
  };

  const [formData, setFormData] = useState<SchoolWebsiteData>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData(initialFormState);
    }
    setErrors({});
  }, [initialData, open, schoolId]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleContactInfoChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: { ...prev.contactInfo, [field]: value }
    }));
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...(formData.features || [])];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setFormData(prev => ({ ...prev, features: [...(prev.features || []), ''] }));
  };

  const removeFeature = (index: number) => {
    const newFeatures = formData.features?.filter((_, i) => i !== index) || [];
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const handleSubmit = () => {
    // Basic validation
    const newErrors: Record<string, string> = {};
    
    if (!formData.title?.trim()) newErrors.title = t('title_required');
    if (!formData.description?.trim()) newErrors.description = t('description_required');
    if (!formData.aboutSection?.trim()) newErrors.aboutSection = t('about_section_required');
    if (!formData.contactInfo?.address?.trim()) newErrors.address = t('address_required');
    if (!formData.contactInfo?.phone?.trim()) newErrors.phone = t('phone_required');
    if (!formData.contactInfo?.email?.trim()) newErrors.email = t('email_required');

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          {isEdit ? t('edit_school_website') : t('create_school_website')}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t('basic_information')}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label={t('website_title')}
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              fullWidth
              error={!!errors.title}
              helperText={errors.title}
              margin="normal"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label={t('website_description')}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              error={!!errors.description}
              helperText={errors.description}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('about_section')}
              value={formData.aboutSection}
              onChange={(e) => handleChange('aboutSection', e.target.value)}
              fullWidth
              multiline
              rows={4}
              error={!!errors.aboutSection}
              helperText={errors.aboutSection}
              margin="normal"
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t('contact_information')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('address')}
              value={formData.contactInfo?.address}
              onChange={(e) => handleContactInfoChange('address', e.target.value)}
              fullWidth
              error={!!errors.address}
              helperText={errors.address}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('phone')}
              value={formData.contactInfo?.phone}
              onChange={(e) => handleContactInfoChange('phone', e.target.value)}
              fullWidth
              error={!!errors.phone}
              helperText={errors.phone}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('email')}
              value={formData.contactInfo?.email}
              onChange={(e) => handleContactInfoChange('email', e.target.value)}
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('website')}
              value={formData.contactInfo?.website}
              onChange={(e) => handleContactInfoChange('website', e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid>

          {/* Features */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {t('school_features')}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addFeature}
                variant="outlined"
                size="small"
              >
                {t('add_feature')}
              </Button>
            </Box>
          </Grid>

          {formData.features?.map((feature, index) => (
            <Grid item xs={12} key={index}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={`${t('feature')} ${index + 1}`}
                  value={feature}
                  onChange={(e) => handleFeatureChange(index, e.target.value)}
                  fullWidth
                  margin="normal"
                />
                {formData.features?.length && formData.features?.length > 1 && (
                  <IconButton
                    onClick={() => removeFeature(index)}
                    color="error"
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Grid>
          ))}

          {/* Custom Styling */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t('custom_styling')}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('custom_css')}
              value={formData.customCss}
              onChange={(e) => handleChange('customCss', e.target.value)}
              fullWidth
              multiline
              rows={6}
              margin="normal"
              helperText={t('custom_css_help')}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label={t('custom_javascript')}
              value={formData.customJs}
              onChange={(e) => handleChange('customJs', e.target.value)}
              fullWidth
              multiline
              rows={6}
              margin="normal"
              helperText={t('custom_js_help')}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
              label={t('website_active')}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {isEdit ? t('update') : t('create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchoolWebsiteFormModal; 